<template>
  <BaseModal :modalTitle="$t('area.text-list-item.create.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit"
      :resetCallback="formResetCallback">
      <UiInputTextFieldVee
        name="itemText"
        :label="$t('term.text')"
        :placeholder="$t('area.text-list-item.placeholder.text')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.itemText" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useTextListStore } from '@/stores/textListStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { focusFirstElement } from '@/globals/typescript/utils/focusManagement'

export default {
  name: 'CreateTextListItemModal',

  props: {
    listID: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        itemText: '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useTextListStore, ['allTextLists']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    currentListItems(): Record<string, { text: Record<string, string> }> {
      const list = this.allTextLists.find(
        (l) =>
          l.id === this.listID ||
          ('systemKey' in l && l.systemKey === this.listID)
      )
      return (
        (list?.items as Record<string, { text: Record<string, string> }>) ?? {}
      )
    },
  },

  watch: {
    'form.itemText'() {
      this.errorText = ''
    },
  },

  methods: {
    ...mapActions(useTextListStore, ['addTextListItem']),

    async onSubmit() {
      this.errorText = ''

      const text = this.form.itemText.trim()

      const isDuplicate = Object.values(this.currentListItems).some((item) =>
        Object.values(item.text).some(
          (val) => val.toLowerCase() === text.toLowerCase()
        )
      )

      if (isDuplicate) {
        this.errorText = this.$t('area.text-list-item.message.duplicate')
        return
      }

      this.isLoading = true

      try {
        await this.addTextListItem(this.listID, text)
        useToast().success(this.$t('W: Element tilføjet'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke tilføje element'))
      } finally {
        this.isLoading = false
      }
    },

    // This modal is the app's only stay-open-and-reset form, so it is the only
    // consumer whose resetCallback does more than answer "did it succeed".
    // UiBaseFormVee calls this after onSubmit() has resolved and after it has
    // blurred the active element, then calls vee-validate's own resetForm()
    // if we return true.
    formResetCallback(): boolean {
      // Mirrors the 12 other consumers: a failed add must keep the typed text
      // and the error on screen, so only a success resets.
      const didSucceed = !this.errorText

      if (didSucceed) {
        this.form.itemText = ''

        // The baseline has to be taken *here*, after the field is cleared —
        // never inside onSubmit(), which resolves before the reset runs. That
        // ordering is the whole of MIL-291: a baseline holding the text just
        // saved leaves the modal permanently dirty (see the Unsaved Changes
        // False Positive After Add plan).
        this.unsavedGuard.takeSnapshot()
      }

      // UiBaseFormVee blurs the active element before calling this, so the
      // cursor has to be put back on *both* paths — after a failed add the
      // typed text is still there and the user needs to correct it, and
      // without this focus is left on BODY. Deferred so it lands after
      // resetForm(), and routed through the same helper ModalHost uses on
      // open, which applies the silent-focus class so no ring appears.
      this.$nextTick(() => {
        focusFirstElement(this.$el as HTMLElement)
      })

      return didSucceed
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
